import React, { useState } from "react";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { Mail, Copy } from "lucide-react";
import { toast } from "sonner";

const TYPES = ["onboarding", "re-engagement", "ppv", "mass-promo", "subscription-reminder"];

export default function Sequences() {
  const [type, setType] = useState("onboarding");
  const [brief, setBrief] = useState("");
  const [lang, setLang] = useState("auto");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    if (!brief.trim()) return;
    setBusy(true); setRes(null);
    try {
      const r = await api.post("/api/sequences/generate", { sequence_type: type, brief, language: lang });
      setRes(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };

  return (
    <AppLayout title="DM sequences" overline="060 · AUTOMATED FLOWS · ONLYFANS / JFF">
      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6">
        <section className="border border-zinc-900 bg-zinc-950 p-6 fade-up">
          <div className="overline mb-3">Sequence type</div>
          <div className="flex flex-wrap gap-2 mb-6">
            {TYPES.map((t) => (
              <button key={t} onClick={() => setType(t)} data-testid={`seq-type-${t}`}
                className={`px-3 py-2 text-[11px] font-mono-ui uppercase tracking-wider border transition ${
                  type === t ? "border-[color:var(--gio-primary)] text-[color:var(--gio-primary)]" : "border-zinc-800 text-zinc-500 hover:text-white"
                }`}>{t}</button>
            ))}
          </div>

          <div className="overline mb-3">Brief</div>
          <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={6}
            placeholder="New subs who bought at 50% promo, want to upsell custom video within 3 days..."
            data-testid="seq-brief"
            className="w-full bg-black border border-zinc-900 text-white p-3 text-sm focus:outline-none focus:border-[color:var(--gio-primary)]" />

          <div className="overline mt-6 mb-3">Language</div>
          <div className="flex gap-2 mb-6">
            {["auto", "en", "el"].map((l) => (
              <button key={l} onClick={() => setLang(l)} data-testid={`seq-lang-${l}`}
                className={`px-3 py-2 text-[11px] font-mono-ui uppercase tracking-wider border transition ${
                  lang === l ? "border-[color:var(--gio-primary)] text-[color:var(--gio-primary)]" : "border-zinc-800 text-zinc-500 hover:text-white"
                }`}>{l === "auto" ? "AUTO" : l === "en" ? "EN" : "ΕΛΛ"}</button>
            ))}
          </div>

          <button onClick={generate} disabled={busy || !brief.trim()} data-testid="seq-generate-button"
            className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-40">
            <Mail size={16} /> {busy ? "Drafting..." : "Build sequence"}
          </button>
        </section>

        <section className="space-y-3" data-testid="seq-results">
          {!res && !busy && (
            <div className="border border-dashed border-zinc-900 p-12 text-center text-zinc-600 text-sm">
              A 3–5 step drip sequence will appear here.
            </div>
          )}
          {busy && <div className="overline text-[color:var(--gio-primary)] p-6">DRAFTING STEPS...</div>}
          {res?.messages?.map((m, i) => (
            <div key={i} className="border border-zinc-900 bg-zinc-950 p-5 fade-up" data-testid={`seq-step-${m.step}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="overline text-[color:var(--gio-primary)]">STEP {m.step} · {m.delay}</div>
                <button onClick={() => copy(m.text)} data-testid={`seq-copy-${m.step}`} className="text-zinc-500 hover:text-[color:var(--gio-primary)] text-xs font-mono-ui flex items-center gap-1">
                  <Copy size={14} /> COPY
                </button>
              </div>
              <div className="text-white text-sm leading-relaxed whitespace-pre-wrap mb-2">{m.text}</div>
              {m.intent && <div className="overline text-zinc-500">INTENT · {m.intent}</div>}
            </div>
          ))}
          {res?.notes && (
            <div className="border border-[color:var(--gio-primary)]/40 bg-zinc-950 p-4 text-sm text-[color:var(--gio-primary)]" data-testid="seq-notes">
              <span className="overline mr-2">NOTE</span>{res.notes}
            </div>
          )}
          {res && !res.messages?.length && res.raw && <pre className="border border-zinc-900 bg-zinc-950 p-4 text-xs text-zinc-400 whitespace-pre-wrap">{res.raw}</pre>}
        </section>
      </div>
    </AppLayout>
  );
}
